// ConfirmDialog — shared destructive-confirm modal.
//
// Two-button portal-rendered dialog with Escape / backdrop / focus
// trap behaviour. Used by Saved (clear all) and Unreads (mark all
// read). Lives in its own module so consumers don't depend on
// SavedView's exported internals — a sibling import would couple two
// unrelated views.

import { useEffect, useRef, type CSSProperties } from "react";
import { createPortal } from "react-dom";

import { usePortalRoot } from "~/components/EmbedPortalContext";
import { isEmbeddedPortal } from "~/lib/portal-positioning";

export interface ConfirmDialogProps {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Destructive-confirm dialog. Renders through `usePortalRoot` so the
 * scrim either covers the embed bounding box (when embedded) or the
 * viewport (standalone). Focus parks on Cancel, traps between Cancel
 * and Confirm via Tab / Shift+Tab, and restores to the opener on
 * close — the parts of the APG dialog pattern that aren't free.
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const portalRoot = usePortalRoot();

  useEffect(() => {
    previouslyFocusedRef.current =
      typeof document !== "undefined" ? (document.activeElement as HTMLElement | null) : null;
    cancelRef.current?.focus();
    return () => {
      previouslyFocusedRef.current?.focus?.();
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onCancel();
      return;
    }
    if (e.key === "Tab") {
      // Two-button trap: Shift+Tab from Cancel wraps to Confirm and
      // vice versa. Saves us from pulling in a full focus-trap lib
      // for a two-control dialog.
      const cancel = cancelRef.current;
      const confirm = confirmRef.current;
      if (!cancel || !confirm) return;
      const active = document.activeElement;
      if (e.shiftKey && active === cancel) {
        e.preventDefault();
        confirm.focus();
      } else if (!e.shiftKey && active === confirm) {
        e.preventDefault();
        cancel.focus();
      }
    }
  };

  if (!portalRoot) return null;

  const scrimPosition: "fixed" | "absolute" = isEmbeddedPortal(portalRoot) ? "absolute" : "fixed";

  const overlay = (
    <div
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      onKeyDown={handleKeyDown}
      style={{
        position: scrimPosition,
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--chat-scrim-dialog)",
        zIndex: 900,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-body"
        style={{
          width: "min(var(--chat-dialog-confirm-width), calc(100% - 2 * var(--chat-space-4)))",
          boxSizing: "border-box",
          padding: "var(--chat-space-5)",
          borderRadius: "var(--chat-radius-xl)",
          background: "var(--chat-surface-dialog)",
          boxShadow: "var(--chat-shadow-dialog)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--chat-space-3)",
        }}
      >
        <h3
          id="confirm-dialog-title"
          style={{
            margin: 0,
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-display)",
            fontSize: "var(--chat-type-display-sm-size)",
            lineHeight: "var(--chat-type-display-sm-line)",
            fontWeight: 500,
          }}
        >
          {title}
        </h3>
        <p
          id="confirm-dialog-body"
          style={{
            margin: 0,
            color: "var(--chat-fg-secondary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
          }}
        >
          {body}
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "var(--chat-space-2)",
            marginTop: "var(--chat-space-2)",
          }}
        >
          <button
            ref={cancelRef}
            type="button"
            className="chat-button"
            onClick={onCancel}
            style={ghostButtonStyle}
          >
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            className="chat-button"
            onClick={onConfirm}
            style={dangerButtonStyle}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, portalRoot);
}

const ghostButtonStyle: CSSProperties = {
  padding: "var(--chat-space-1) var(--chat-space-3)",
  borderRadius: "var(--chat-radius-sm)",
  border: "none",
  background: "transparent",
  color: "var(--chat-fg-secondary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
  fontWeight: 500,
  cursor: "pointer",
};

const dangerButtonStyle: CSSProperties = {
  padding: "var(--chat-space-1) var(--chat-space-3)",
  borderRadius: "var(--chat-radius-sm)",
  border: "none",
  background: "transparent",
  color: "var(--chat-state-danger)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
  fontWeight: 500,
  cursor: "pointer",
};
